import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAccountApi } from '../../hooks/use-settings-api';
import { clearTwoFactorSecretQueries } from './use-enable-two-factor';

/**
 * Replaces the user's authenticator with a freshly issued, *unconfirmed* secret.
 *
 * Disable-then-enable rather than Fortify's `force` flag, which overwrites the secret in place
 * but leaves `two_factor_confirmed_at` set: the account would immediately be challenged against
 * a secret nobody has scanned, so abandoning the flow (closing the tab, navigating away) would
 * lock the user out for good - their old authenticator and old recovery codes are dead either
 * way. Disabling first clears the confirmation too, so the worst case is an account back to
 * password-only that the user can re-enrol themselves.
 *
 * The two calls are safe to retry as a pair: disabling an already-disabled factor is a no-op,
 * and enabling only issues a secret when there is none.
 */
export function useResetTwoFactor() {
  const { accountApi } = useAccountApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await accountApi.disableTwoFactor();
      await accountApi.enableTwoFactor();
    },
    onSuccess: () => clearTwoFactorSecretQueries(queryClient),
  });
}
